<template>
  <div>
    <el-table
      :border="true"
      :data="rows"
      @sort-change="doChangeSort"
      ref="table"
      row-key="id"
      v-loading="loading"
    >
      <el-table-column type="selection" width="55"></el-table-column>

      <el-table-column v-if="hasPermissionToAuditLogs" :label="fields.id.label"  :prop="fields.id.name" sortable="custom">
        <template slot-scope="scope">{{ presenter(scope.row, 'id') }}</template>
      </el-table-column>

      <el-table-column :label="fields.awayTeam.label" :prop="fields.awayTeam.name">
        <template slot-scope="scope">
          <app-list-item-relation-to-one
            :label="fields.awayTeam.label"
            :permission="fields.awayTeam.readPermission"
            :url="fields.awayTeam.viewUrl"
            :value="presenter(scope.row, 'awayTeam')"
          ></app-list-item-relation-to-one>
        </template>
      </el-table-column>

      <el-table-column :label="fields.localTeam.label" :prop="fields.localTeam.name">
        <template slot-scope="scope">
          <app-list-item-relation-to-one
            :label="fields.localTeam.label"
            :permission="fields.localTeam.readPermission"
            :url="fields.localTeam.viewUrl"
            :value="presenter(scope.row, 'localTeam')"
          ></app-list-item-relation-to-one>
        </template>
      </el-table-column>

      <el-table-column
        :label="fields.date.label"
        :prop="fields.date.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'date') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.hour.label"
        :prop="fields.hour.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'hour') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.duration.label"
        :prop="fields.duration.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'duration') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.location.label"
        :prop="fields.location.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'location') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.status.label"
        :prop="fields.status.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'status') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column>

      <el-table-column :fixed="isMobile? undefined : 'right'" align="center" width="180">
        <template slot-scope="scope">
          <div class="table-actions">
            <router-link :to="`/game/${scope.row.id}`">
              <el-button type="text">
                <app-i18n code="common.view"></app-i18n>
              </el-button>
            </router-link>

            <router-link :to="`/game/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button type="text">
                <app-i18n code="common.edit"></app-i18n>
              </el-button>
            </router-link>

            <el-button
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              type="text"
              v-if="hasPermissionToDestroy"
            >
              <app-i18n code="common.destroy"></app-i18n>
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { GameModel } from '@modules/game/game-model';
import { mapGetters, mapActions } from 'vuex';
import { GamePermissions } from '@modules/game/game-permissions';
import { i18n } from '@shared/i18n';

export default {
  name: 'app-game-list-table',

  mounted() {
    this.doMountTable(this.$refs.table);
  },

  computed: {
    ...mapGetters({
      rows: 'game/list/rows',
      count: 'game/list/count',
      loading: 'game/list/loading',
      pagination: 'game/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'game/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      dateFormat: 'auth/manage/dateFormat',
      timeFormat: 'auth/manage/timeFormat',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new GamePermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new GamePermissions(this.currentUser).destroy;
    },

    fields() {
      const { dateFormat, timeFormat } = this;
      return new GameModel({ dateFormat, timeFormat }).fields();
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'game/list/doChangeSort',
      doChangePaginationCurrentPage:
        'game/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'game/list/doChangePaginationPageSize',
      doMountTable: 'game/list/doMountTable',
      doDestroy: 'game/destroy/doDestroy',
    }),

    presenter(row, fieldName) {
      const { dateFormat, timeFormat } = this;
      return new GameModel({ dateFormat, timeFormat }).presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },
  },
};
</script>

<style>
</style>
